/**
 * Admin Cohorts Tab
 * Cohort management for the admin console
 */

import { useState, useCallback, useMemo } from "react";
import {
  useCohorts,
  useCreateCohort,
  useUpdateCohort,
  useDeleteCohort,
  useCohortMembers,
  useAddCohortMembers,
  useRemoveCohortMember,
  useSyncGoogleGroup,
} from "@hooks/useCohorts";
import {
  DataTable,
  type Column,
  ConfirmModal,
  SearchInput,
  StatusBadge,
  Modal,
} from "@components/admin";
import { Button } from "@components/common";
import { formatDate } from "@utils/format";
import type {
  Cohort,
  CohortStatus,
  CohortMember,
  CohortCreateRequest,
  CohortUpdateRequest,
} from "@/types/cohorts";
import { showToast } from "@stores/uiStore";
import { generateInviteLink } from "@services/cohorts";

type SortDirection = "asc" | "desc";

// Status badge variant mapping
const statusVariants: Record<CohortStatus, "success" | "warning" | "neutral"> =
  {
    active: "success",
    draft: "warning",
    completed: "success",
    archived: "neutral",
  };

export function AdminCohortsTab() {
  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<CohortStatus | "all">("all");
  const [sortColumn, setSortColumn] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Cohort Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingCohort, setEditingCohort] = useState<Cohort | null>(null);
  const [deletingCohort, setDeletingCohort] = useState<Cohort | null>(null);

  // Member Management
  const [managingMembersFor, setManagingMembersFor] = useState<Cohort | null>(
    null
  );
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [newMemberEmails, setNewMemberEmails] = useState("");
  const [removingMember, setRemovingMember] = useState<CohortMember | null>(
    null
  );

  // Invite Link
  const [showingInviteFor, setShowingInviteFor] = useState<Cohort | null>(null);

  // Form state
  const [formData, setFormData] = useState<CohortCreateRequest>({
    name: "",
    description: "",
    status: "draft",
    max_participants: undefined,
  });

  // Queries & mutations
  const { data: cohortsData, isLoading, refetch } = useCohorts();
  const createCohort = useCreateCohort();
  const updateCohort = useUpdateCohort();
  const deleteCohort = useDeleteCohort();

  // Members queries - only fetch when managing
  const { data: members = [], refetch: refetchMembers } = useCohortMembers(
    managingMembersFor?.id
  );
  const addMembers = useAddCohortMembers();
  const removeMember = useRemoveCohortMember();
  const syncGroup = useSyncGoogleGroup();

  // Filter and sort cohorts
  const filteredCohorts = useMemo(() => {
    const cohorts = cohortsData ?? [];
    let result = [...cohorts];

    // Filter by search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (cohort) =>
          cohort.name.toLowerCase().includes(searchLower) ||
          (cohort.description?.toLowerCase().includes(searchLower) ?? false)
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      result = result.filter((cohort) => cohort.status === statusFilter);
    }

    // Sort
    result.sort((a, b) => {
      const aVal = (a as unknown as Record<string, unknown>)[sortColumn];
      const bVal = (b as unknown as Record<string, unknown>)[sortColumn];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison: number;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else if (sortColumn.includes("date") || sortColumn.includes("At")) {
        comparison =
          new Date(aVal as string).getTime() -
          new Date(bVal as string).getTime();
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return result;
  }, [cohortsData, search, statusFilter, sortColumn, sortDirection]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortColumn(column);
        setSortDirection("asc");
      }
    },
    [sortColumn]
  );

  // Table columns
  const columns: Column<Cohort>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        render: (cohort) => (
          <div>
            <div className="font-medium text-gray-900 dark:text-white">
              {cohort.name}
            </div>
            {cohort.description && (
              <div className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                {cohort.description.slice(0, 60)}
                {cohort.description.length > 60 ? "..." : ""}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        width: "100px",
        render: (cohort) => (
          <StatusBadge
            label={cohort.status.charAt(0).toUpperCase() + cohort.status.slice(1)}
            variant={statusVariants[cohort.status]}
          />
        ),
      },
      {
        key: "memberCount",
        header: "Members",
        sortable: true,
        width: "100px",
        render: (cohort) => (
          <span className="text-gray-600 dark:text-gray-400">
            {cohort.memberCount}
            {cohort.maxParticipants && ` / ${cohort.maxParticipants}`}
          </span>
        ),
      },
      {
        key: "startDate",
        header: "Start Date",
        sortable: true,
        width: "120px",
        render: (cohort) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {cohort.startDate ? formatDate(cohort.startDate) : "-"}
          </span>
        ),
      },
      {
        key: "googleGroupEmail",
        header: "Google Group",
        width: "180px",
        render: (cohort) => (
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
            {cohort.googleGroupEmail || "-"}
          </span>
        ),
      },
      {
        key: "createdAt",
        header: "Created",
        sortable: true,
        width: "120px",
        render: (cohort) => (
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            {formatDate(cohort.createdAt)}
          </span>
        ),
      },
      {
        key: "actions",
        header: "",
        width: "200px",
        render: (cohort) => (
          <div className="flex justify-end gap-1">
            {/* Manage Members */}
            <button
              onClick={() => setManagingMembersFor(cohort)}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Manage members"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </button>
            {/* Invite Link */}
            <button
              onClick={() => setShowingInviteFor(cohort)}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Copy invite link"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                />
              </svg>
            </button>
            {/* Edit */}
            <button
              onClick={() => {
                setEditingCohort(cohort);
                setFormData({
                  name: cohort.name,
                  description: cohort.description || "",
                  status: cohort.status,
                  max_participants: cohort.maxParticipants || undefined,
                  start_date: cohort.startDate || undefined,
                  end_date: cohort.endDate || undefined,
                });
              }}
              className="p-1.5 text-gray-400 hover:text-primary-500 transition-colors"
              title="Edit cohort"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>
            {/* Delete */}
            <button
              onClick={() => setDeletingCohort(cohort)}
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              title="Delete cohort"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  // CRUD handlers
  const handleCreateCohort = async () => {
    try {
      await createCohort.mutateAsync(formData);
      showToast("Cohort created successfully", "success");
      setIsCreateModalOpen(false);
      setFormData({
        name: "",
        description: "",
        status: "draft",
        max_participants: undefined,
      });
    } catch (error) {
      showToast("Failed to create cohort", "error");
    }
  };

  const handleUpdateCohort = async () => {
    if (!editingCohort) return;
    try {
      await updateCohort.mutateAsync({
        id: editingCohort.id,
        data: formData as CohortUpdateRequest,
      });
      showToast("Cohort updated successfully", "success");
      setEditingCohort(null);
    } catch (error) {
      showToast("Failed to update cohort", "error");
    }
  };

  const handleDeleteCohort = async () => {
    if (!deletingCohort) return;
    try {
      await deleteCohort.mutateAsync(deletingCohort.id);
      showToast("Cohort deleted successfully", "success");
      setDeletingCohort(null);
    } catch (error) {
      showToast("Failed to delete cohort", "error");
    }
  };

  // Member handlers
  const handleAddMembers = async () => {
    if (!managingMembersFor || !newMemberEmails.trim()) return;
    const emails = newMemberEmails
      .split(/[,\n]/)
      .map((e) => e.trim())
      .filter((e) => e);

    try {
      await addMembers.mutateAsync({
        cohortId: managingMembersFor.id,
        data: { emails },
      });
      showToast(`Added ${emails.length} member(s)`, "success");
      setNewMemberEmails("");
      setIsAddMembersOpen(false);
      refetchMembers();
    } catch (error) {
      showToast("Failed to add members", "error");
    }
  };

  const handleRemoveMember = async () => {
    if (!removingMember || !managingMembersFor) return;
    try {
      await removeMember.mutateAsync({
        cohortId: managingMembersFor.id,
        memberId: removingMember.id,
      });
      showToast("Member removed", "success");
      setRemovingMember(null);
      refetchMembers();
    } catch (error) {
      showToast("Failed to remove member", "error");
    }
  };

  const handleSyncGroup = async () => {
    if (!managingMembersFor) return;
    try {
      const result = await syncGroup.mutateAsync(managingMembersFor.id);
      showToast(
        `Synced: ${result.added} added, ${result.removed} removed`,
        "success"
      );
    } catch (error) {
      showToast("Failed to sync Google Group", "error");
    }
  };

  const copyInviteLink = () => {
    if (!showingInviteFor) return;
    const link = generateInviteLink(showingInviteFor.inviteCode);
    navigator.clipboard.writeText(link);
    showToast("Invite link copied to clipboard", "success");
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Cohorts Management
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create and manage workshop cohorts with Google Group integration
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Cohort
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search cohorts..."
          className="w-64"
        />
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as CohortStatus | "all")
          }
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
        <button
          onClick={() => refetch()}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Refresh"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredCohorts}
        columns={columns}
        keyExtractor={(cohort) => cohort.id}
        isLoading={isLoading}
        emptyMessage="No cohorts found"
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      {/* Create/Edit Cohort Modal */}
      <Modal
        isOpen={isCreateModalOpen || !!editingCohort}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingCohort(null);
          setFormData({
            name: "",
            description: "",
            status: "draft",
            max_participants: undefined,
          });
        }}
        title={editingCohort ? "Edit Cohort" : "Create Cohort"}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="January 2026 - Intro Workshop"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Cohort description..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as CohortStatus,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Participants
              </label>
              <input
                type="number"
                value={formData.max_participants || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    max_participants: e.target.value
                      ? parseInt(e.target.value)
                      : undefined,
                  })
                }
                min="1"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="No limit"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                setEditingCohort(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editingCohort ? handleUpdateCohort : handleCreateCohort}
              isLoading={createCohort.isPending || updateCohort.isPending}
            >
              {editingCohort ? "Save Changes" : "Create Cohort"}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={!!deletingCohort}
        onClose={() => setDeletingCohort(null)}
        onConfirm={handleDeleteCohort}
        title="Delete Cohort"
        message={`Are you sure you want to delete "${deletingCohort?.name}"? This will also delete the associated Google Group. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteCohort.isPending}
      />

      {/* Member Management Modal */}
      <Modal
        isOpen={!!managingMembersFor}
        onClose={() => setManagingMembersFor(null)}
        title={`Members: ${managingMembersFor?.name ?? ""}`}
        description={`Google Group: ${managingMembersFor?.googleGroupEmail || "Not created"}`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Action buttons */}
          <div className="flex justify-between">
            <Button size="sm" onClick={() => setIsAddMembersOpen(true)}>
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                />
              </svg>
              Add Members
            </Button>
            {managingMembersFor?.googleGroupEmail && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleSyncGroup}
                isLoading={syncGroup.isPending}
              >
                Sync Google Group
              </Button>
            )}
          </div>

          {/* Members list */}
          {members.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No members yet. Add members to get started.</p>
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
              {members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-3"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {member.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      Enrolled: {formatDate(member.enrolledAt)}
                      {member.userId ? " (Registered)" : " (Pending)"}
                    </div>
                  </div>
                  <button
                    onClick={() => setRemovingMember(member)}
                    className="p-1.5 text-gray-400 hover:text-red-500"
                    title="Remove member"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button variant="ghost" onClick={() => setManagingMembersFor(null)}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Add Members Modal */}
      <Modal
        isOpen={isAddMembersOpen}
        onClose={() => {
          setIsAddMembersOpen(false);
          setNewMemberEmails("");
        }}
        title="Add Members"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Addresses
            </label>
            <textarea
              value={newMemberEmails}
              onChange={(e) => setNewMemberEmails(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              placeholder="Enter email addresses, one per line or comma-separated"
            />
            <p className="mt-1 text-xs text-gray-500">
              Members will be added to the cohort's Google Group for GCP access.
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="ghost"
              onClick={() => {
                setIsAddMembersOpen(false);
                setNewMemberEmails("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              isLoading={addMembers.isPending}
              disabled={!newMemberEmails.trim()}
            >
              Add Members
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Member Confirmation */}
      <ConfirmModal
        isOpen={!!removingMember}
        onClose={() => setRemovingMember(null)}
        onConfirm={handleRemoveMember}
        title="Remove Member"
        message={`Are you sure you want to remove "${removingMember?.email}" from this cohort?`}
        confirmLabel="Remove"
        variant="danger"
        isLoading={removeMember.isPending}
      />

      {/* Invite Link Modal */}
      <Modal
        isOpen={!!showingInviteFor}
        onClose={() => setShowingInviteFor(null)}
        title="Invite Link"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Share this link with participants to allow them to self-enroll in
            the cohort.
          </p>
          <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg font-mono text-sm break-all">
            {showingInviteFor && generateInviteLink(showingInviteFor.inviteCode)}
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowingInviteFor(null)}>
              Close
            </Button>
            <Button onClick={copyInviteLink}>Copy Link</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
